import React from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ArrowRight, Check, Sparkles } from 'lucide-react';
import { OPERATIONAL_SERVICES, PROCESS_SERVICES, PRICING_PLANS } from '../data/mock';
import FinalCTA from '../components/FinalCTA';

const SERVICE_FEATURES = [
  'Dedicated specialist on your team',
  'Onboarded in 7 days',
  'Transparent INR pricing',
];

const PageHero = ({ eyebrow, title, highlight, sub }) => (
  <section className="relative pt-36 pb-16 overflow-hidden">
    <div className="absolute inset-0 bg-radial-glow pointer-events-none" />
    <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
    <div className="relative max-w-4xl mx-auto px-6 text-center">
      <p className="text-[#1AB6FF] font-semibold uppercase tracking-[0.2em] text-xs mb-4">{eyebrow}</p>
      <h1 className="font-display font-bold text-5xl md:text-6xl leading-[1.05]">
        {title} <span className="gradient-text">{highlight}</span>
      </h1>
      <p className="mt-6 text-white/65 text-lg max-w-2xl mx-auto">{sub}</p>
    </div>
  </section>
);

const ServiceCard = ({ service }) => {
  const Icon = Icons[service.icon] || Sparkles;
  return (
    <div className="card-tech rounded-2xl p-6 group">
      <div className="w-14 h-14 rounded-full border-2 border-[#1AB6FF]/30 bg-[#1AB6FF]/5 flex items-center justify-center mb-5">
        <Icon size={24} className="text-[#1AB6FF]" />
      </div>
      <h3 className="font-display font-semibold text-xl text-white">{service.title}</h3>
      <p className="mt-2 text-white/55 text-sm">{service.desc}</p>
      <ul className="mt-5 space-y-2 text-sm text-white/70">
        {SERVICE_FEATURES.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <Check size={16} className="text-[#1AB6FF] mt-0.5 shrink-0" />
            {f}
          </li>
        ))}
      </ul>
    </div>
  );
};

const ServicesGrid = () => {
  const all = [...OPERATIONAL_SERVICES, ...PROCESS_SERVICES];
  return (
    <section className="py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {all.map((s) => (
            <ServiceCard key={s.title} service={s} />
          ))}
        </div>
      </div>
    </section>
  );
};

const PricingCard = ({ plan }) => (
  <div
    className={`card-tech rounded-2xl p-8 flex flex-col ${plan.highlight ? 'border-[#1AB6FF]/60 glow-cyan' : ''}`}
  >
    {plan.highlight && (
      <span className="self-start mb-3 px-3 py-1 rounded-full text-[10px] uppercase tracking-wider bg-[#1AB6FF] text-[#04070d] font-semibold">
        Most Popular
      </span>
    )}
    <p className="font-display font-semibold text-2xl text-white">{plan.name}</p>
    <p className="text-white/55 text-sm mt-1">{plan.hours} <span className="text-white/40">{plan.sub}</span></p>
    <p className="mt-6 font-display font-bold text-4xl text-white">
      {plan.price}
      <span className="text-base font-normal text-white/55 ml-1">{plan.period}</span>
    </p>
    <p className="mt-3 text-white/65 text-sm">{plan.desc}</p>
    <ul className="mt-6 space-y-3 text-sm text-white/75 flex-1">
      {plan.features.map((f) => (
        <li key={f} className="flex items-start gap-2">
          <Check size={16} className="text-[#1AB6FF] mt-0.5 shrink-0" />
          {f}
        </li>
      ))}
    </ul>
    <Link
      to="/contact"
      className={`mt-8 rounded-full px-5 py-3 text-sm font-semibold text-center inline-flex items-center justify-center gap-2 ${plan.highlight ? 'btn-cyan' : 'btn-outline-cyan'}`}
    >
      {plan.cta} <ArrowRight size={16} />
    </Link>
  </div>
);

const PricingSection = () => (
  <section className="py-20">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="text-center mb-14">
        <p className="text-[#1AB6FF] font-semibold uppercase tracking-[0.2em] text-xs mb-3">Plans &amp; Pricing</p>
        <h2 className="font-display font-bold text-4xl md:text-5xl leading-tight">
          Flexible plans for <span className="gradient-text">every Indian business.</span>
        </h2>
        <p className="mt-4 text-white/55">All prices in INR. No long-term contracts. GST as applicable.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {PRICING_PLANS.map((p) => (
          <PricingCard key={p.name} plan={p} />
        ))}
      </div>
    </div>
  </section>
);

const ServicesPage = () => (
  <div>
    <PageHero
      eyebrow="Our Services"
      title="Everything you need to"
      highlight="operate at scale."
      sub="From dedicated virtual assistants to bespoke AI workflows — pick a service or combine them into a complete operational engine."
    />
    <ServicesGrid />
    <PricingSection />
    <FinalCTA />
  </div>
);

export default ServicesPage;
